// Live GitHub data for the `projects` command and the status bar.
// Unauthenticated public API — rate limited to 60 req/hr per IP, so
// results are cached in sessionStorage for the tab's lifetime.

import { useState, useEffect, useCallback } from 'react';

const USER = 'attuulGeriyan';
const CACHE_KEY = `gh:${USER}`;

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (data) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    /* private mode, quota, or blocked storage — the page works without it */
  }
};

export function useGitHub() {
  const [state, setState] = useState(() => {
    const cached = readCache();
    return cached
      ? { status: 'ready', repos: cached.repos, lastPush: cached.lastPush }
      : { status: 'loading', repos: [], lastPush: null };
  });

  // A failed fetch shouldn't strand the tab forever — `retry` lets a
  // later `projects` run try again.
  const retry = useCallback(() => {
    setState((prev) => (prev.status === 'error'
      ? { status: 'loading', repos: [], lastPush: null }
      : prev));
  }, []);

  useEffect(() => {
    if (state.status !== 'loading') return undefined;

    let cancelled = false;
    const controller = new AbortController();

    fetch(`https://api.github.com/users/${USER}/repos?sort=updated&per_page=100`, {
      signal: controller.signal,
      headers: { Accept: 'application/vnd.github+json' }
    })
      .then((res) => {
        if (!res.ok) throw new Error(`github ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (cancelled || !Array.isArray(json)) return;
        const repos = json
          .filter((r) => !r.fork)
          .map((r) => ({
            name: r.name,
            description: r.description,
            language: r.language,
            stars: r.stargazers_count,
            url: r.html_url,
            pushedAt: r.pushed_at,
            topics: r.topics || []
          }));
        // Only repos we actually list — a fork's push isn't his work.
        const lastPush = repos.reduce(
          (acc, r) => (!acc || r.pushedAt > acc ? r.pushedAt : acc),
          null
        );
        writeCache({ repos, lastPush });
        setState({ status: 'ready', repos, lastPush });
      })
      .catch((err) => {
        if (cancelled || err.name === 'AbortError') return;
        setState({ status: 'error', repos: [], lastPush: null });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [state.status]);

  return { ...state, retry };
}

export function relativeTime(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}
